import { startCase } from 'lodash';
import { FinanceType } from '@upstart/harmony/lib/constants';
import {
  BOOLEAN,
  CURRENCY,
  NUMBER,
  PERCENT,
  PRECISION_MAP,
} from '../Constants';

export const UNKNOWN = 'unknown';

interface FormatValueArgs {
  name: string;
  value: unknown;
  parent?: string;
}

export const formatValue = ({
  name,
  value,
  parent,
}: FormatValueArgs): string => {
  const precision = PRECISION_MAP.get(name);
  if (value === null || value === undefined) {
    if (PERCENT.includes(name)) {
      return '0.00%';
    }
    if (CURRENCY.includes(name)) {
      return '$0.00';
    }
    if (NUMBER.includes(name)) {
      return '0';
    }
    if (BOOLEAN.includes(name)) {
      return 'No';
    }
    return 'none';
  }
  if (typeof value === 'number') {
    if (PERCENT.includes(name)) {
      return `${(value * 100).toFixed(precision ?? 2)}%`;
    }
    // special handling of tax rates because the name 'rate' is
    // also used in lease rates which are formatted as 8-digit decimals
    if (parent === 'taxBreakdown' && name === 'rate') {
      return `${(value * 100).toFixed(2)}%`;
    }
    if (CURRENCY.includes(name)) {
      return value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
    }
    if (precision && precision > 0) {
      return value.toFixed(precision);
    }
    return value.toLocaleString();
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'string') {
    return value;
  }
  return UNKNOWN;
};

export const formatName = (name: string, financeType?: FinanceType): string => {
  switch (name.toLowerCase()) {
    case 'msrp':
      return 'MSRP';
    case 'apr':
      return 'APR';
    case 'mrm':
      return 'MRM';
    case 'programs':
      return 'Incentives';
    case 'daystofirstpayment':
      return 'Days to First Payment';
    case 'selectedproducts':
      return 'Protection Plans';
    case 'selectedaccessories':
      return 'Accessories';
    case 'penciltradeins':
      return 'Trade-Ins';
    case 'fees':
      return 'Dealer Fees';
    case 'dmscode':
      return 'DMS Code';
    case 'faux-trade-in':
      return 'trade-in';
    case 'downpayment':
      if (financeType === FinanceType.CASH) {
        return 'Deposit';
      }
      return startCase(name);
    default:
      return startCase(name);
  }
};
